<template>
  <div class="cron-app">
    <!-- Visual builder -->
    <div class="cron-card cron-form-card">
      <div class="cron-card-head">
        <span class="cron-card-icon">⚙</span>
        <span class="cron-card-title">{{ $t('tool.CronGenerator.builderTitle') }}</span>
        <span class="cron-card-hint">{{ $t('tool.CronGenerator.builderHint') }}</span>
      </div>

      <div class="cron-fields">
        <div class="cron-field" v-for="(f, i) in fields" :key="f.key">
          <div class="cron-field-hd">
            <span class="cron-field-name">{{ fieldName(f.key) }}</span>
            <span class="cron-field-range">{{ f.key==='sec'?'0-59':f.key==='min'?'0-59':f.key==='hour'?'0-23':f.key==='day'?'1-31':f.key==='month'?'1-12':'0-6' }}</span>
          </div>

          <div class="cron-field-presets">
            <button v-for="(p, pi) in presetsFor(f.key)" :key="pi"
              :class="['cron-chip',{on:isPresetActive(f,p)}]"
              @click="applyFieldPreset(f,p)">{{ p.label }}</button>
          </div>

          <div class="cron-field-custom" v-if="f.mode==='custom'" key="custom">
            <div class="cron-custom-row" v-if="f.customType==='every'">
              <span class="cron-custom-lbl">{{ $t('tool.CronGenerator.every') }}</span>
              <input class="cron-inp cron-inp-num" v-model.number="f.everyN" @input="updateCron"/>
              <span class="cron-custom-lbl">{{ fieldUnitName(i) }}</span>
            </div>
            <div class="cron-custom-row" v-else-if="f.customType==='at'">
              <span class="cron-custom-lbl">{{ $t('tool.CronGenerator.specific') }}</span>
              <input class="cron-inp cron-inp-txt" v-model="f.specific" @input="updateCron"
                :placeholder="fieldRangeHint(i)"/>
              <span class="cron-custom-hint" v-if="f.key==='week'">{{ $t('tool.CronGenerator.weekHint') }}</span>
            </div>
            <div class="cron-custom-row" v-else-if="f.customType==='range'">
              <span class="cron-custom-lbl">{{ $t('tool.CronGenerator.from') }}</span><input class="cron-inp cron-inp-num" v-model.number="f.rangeFrom" @input="updateCron"/>
              <span class="cron-custom-lbl">{{ $t('tool.CronGenerator.to') }}</span><input class="cron-inp cron-inp-num" v-model.number="f.rangeTo" @input="updateCron"/>
              <span v-if="f.key==='week'" class="cron-custom-hint">{{ $t('tool.CronGenerator.weekRangeHint') }}</span>
            </div>
            <div class="cron-custom-row" v-else-if="f.customType==='step'">
              <span class="cron-custom-lbl">{{ $t('tool.CronGenerator.start') }}</span><input class="cron-inp cron-inp-num" v-model.number="f.stepFrom" @input="updateCron"/>
              <span class="cron-custom-lbl">{{ $t('tool.CronGenerator.startStep') }}</span><input class="cron-inp cron-inp-num" v-model.number="f.stepBy" @input="updateCron"/>
              <span class="cron-custom-lbl">{{ fieldUnitName(i) }}{{ $t('tool.CronGenerator.stepUnit') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Expression & description -->
    <div class="cron-card cron-expr-card">
      <div class="cron-card-head">
        <span class="cron-card-icon">▸</span>
        <span class="cron-card-title">{{ $t('tool.CronGenerator.expressionAndDesc') }}</span>
      </div>
      <div class="cron-expr-body">
        <div class="cron-expr-display">
          <input class="cron-expr-input" v-model="cronExpr" @input="onExprInput"
            @keyup.enter="parseInput" spellcheck="false"
            :class="{error:exprError}"/>
          <button class="cron-btn cron-btn-copy" @click="copyExpr" :title="$t('tool.CronGenerator.copyExpression')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          </button>
        </div>
        <div class="cron-expr-err" v-if="exprError">{{ exprError }}</div>
        <div class="cron-desc" v-if="description && !exprError">
          <span class="cron-desc-icon">💡</span>
          <span>{{ description }}</span>
        </div>
      </div>
    </div>

    <!-- Next run times -->
    <div class="cron-card cron-result-card">
      <div class="cron-card-head">
        <span class="cron-card-icon">⏱</span>
        <span class="cron-card-title">{{ $t('tool.CronGenerator.next5Runs') }}</span>
        <div class="cron-tz-picker">
          <span class="cron-tz-lbl">{{ $t('tool.CronGenerator.timezone') }}</span>
          <el-select v-model="timezone" size="mini" @change="calcNext" filterable>
            <el-option v-for="tz in i18nTimezones" :key="tz.value" :label="tz.label" :value="tz.value"/>
          </el-select>
        </div>
      </div>
      <div class="cron-next-list">
        <div class="cron-next-item" v-for="(t,i) in nextTimes" :key="i">
          <span class="cron-next-dot">0{{ i+1 }}</span>
          <span class="cron-next-time">{{ t }}</span>
        </div>
        <div class="cron-next-empty" v-if="!nextTimes.length && !exprError">
          {{ $t('tool.CronGenerator.errNoMatch') }}
        </div>
        <div class="cron-next-empty" v-if="exprError">
          {{ $t('tool.CronGenerator.errFixSyntax') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
var TIMEZONE_VALUES = [
  {value:'Asia/Shanghai',key:'tzBeijing'},{value:'Asia/Tokyo',key:'tzTokyo'},{value:'Asia/Seoul',key:'tzSeoul'},
  {value:'Asia/Singapore',key:'tzSingapore'},{value:'Asia/Kolkata',key:'tzMumbai'},{value:'Asia/Dubai',key:'tzDubai'},
  {value:'Europe/London',key:'tzLondon'},{value:'Europe/Paris',key:'tzParis'},{value:'Europe/Berlin',key:'tzBerlin'},
  {value:'Europe/Moscow',key:'tzMoscow'},{value:'America/New_York',key:'tzNewYork'},{value:'America/Chicago',key:'tzChicago'},
  {value:'America/Los_Angeles',key:'tzLosAngeles'},{value:'America/Sao_Paulo',key:'tzSaoPaulo'},
  {value:'Australia/Sydney',key:'tzSydney'},{value:'Pacific/Auckland',key:'tzAuckland'},{value:'UTC',key:'tzUTC'}
]

var PRESET_TYPES = { star:'*', at:'at', range:'range', step:'step', last:'last' }

// Static field definitions (no mutable state)
var FIELD_KEYS = ['sec','min','hour','day','month','week']
var FIELD_RANGEHINTS = ['0,15,30,45','0,15,30,45','9,12,18','1,15','1,6,12','1,3,5']
var FIELD_UNIT_KEYS = ['sec','min','hour','','','']
var FIELD_DEFAULT_RANGES = [[0,59],[0,59],[0,23],[1,31],[1,12],[0,6]]

// Preset definitions with i18n key paths (not labels)
var PRESET_DEFS = {
  sec:[{ik:'presetsSec.everySec',type:'star'},{ik:'presetsSec.every15',type:'step',stepFrom:0,stepBy:15},{ik:'presetsSec.every30',type:'step',stepFrom:0,stepBy:30},{ik:'presetsSec.specific',type:'at'}],
  min:[{ik:'presetsMin.everyMin',type:'star'},{ik:'presetsMin.every5',type:'step',stepFrom:0,stepBy:5},{ik:'presetsMin.every10',type:'step',stepFrom:0,stepBy:10},{ik:'presetsMin.every15',type:'step',stepFrom:0,stepBy:15},{ik:'presetsMin.every30',type:'step',stepFrom:0,stepBy:30},{ik:'presetsMin.specific',type:'at'}],
  hour:[{ik:'presetsHour.everyHour',type:'star'},{ik:'presetsHour.every2',type:'step',stepFrom:0,stepBy:2},{ik:'presetsHour.every6',type:'step',stepFrom:0,stepBy:6},{ik:'presetsHour.every12',type:'step',stepFrom:0,stepBy:12},{ik:'presetsHour.specific',type:'at'}],
  day:[{ik:'presetsDay.everyDay',type:'star'},{ik:'presetsDay.lastDay',type:'last'},{ik:'presetsDay.specific',type:'at'},{ik:'presetsDay.range',type:'range'}],
  month:[{ik:'presetsMonth.everyMonth',type:'star'},{ik:'presetsMonth.specific',type:'at'},{ik:'presetsMonth.range',type:'range'}],
  week:[{ik:'presetsWeek.any',type:'star'},{ik:'presetsWeek.workday',type:'at',atValue:'1,2,3,4,5'},{ik:'presetsWeek.weekend',type:'at',atValue:'0,6'},{ik:'presetsWeek.specific',type:'at'},{ik:'presetsWeek.range',type:'range'}]
}

function makeField(i){
  return {
    key:FIELD_KEYS[i],mode:'*',customType:'',
    everyN:1,specific:'',
    rangeFrom:FIELD_DEFAULT_RANGES[i][0],rangeTo:FIELD_DEFAULT_RANGES[i][1],rangeStep:1,
    stepFrom:0,stepBy:1
  }
}

export default {
  name:'CronGenerator',
  props:{config:{type:Object,default:()=>({})}},
  data:function(){
    return {
      fields:[0,1,2,3,4,5].map(makeField),
      cronExpr:'* * * * * *',description:'',
      nextTimes:[],timezone:'Asia/Shanghai',timezones:TIMEZONE_VALUES,
      exprError:'',_suppressInput:false,
      _presetCache:{},_tzCache:[],_wkCache:{}
    }
  },
  computed:{
    i18nTimezones:function(){ return this._tzCache },
    i18nWeekMap:function(){ return this._wkCache }
  },
  created:function(){this.rebuildCache();this.$watch(function(){return this.$i18n.locale},this.rebuildCache.bind(this))},
  mounted:function(){this.updateCron()},
  methods:{
    rebuildCache:function(){
      var t=this,self=this
      // presets
      var pc={}
      Object.keys(PRESET_DEFS).forEach(function(key){pc[key]=PRESET_DEFS[key].map(function(p){var parts=p.ik.split('.');var obj=t.$t('tool.CronGenerator.'+parts[0]);var label=obj[parts[1]]||p.ik;return{label:label,type:PRESET_TYPES[p.type],stepFrom:p.stepFrom,stepBy:p.stepBy,atValue:p.atValue}})})
      this._presetCache=pc
      // timezones
      this._tzCache=TIMEZONE_VALUES.map(function(tz){return{value:tz.value,label:t.$t('tool.CronGenerator.'+tz.key)||tz.value}})
      // weekdays
      var wm={};for(var i=0;i<7;i++)wm[i]=t.$t('tool.CronGenerator.weekdayNames.'+i);this._wkCache=wm
    },
    fieldName:function(key){ return this.$t('tool.CronGenerator.'+key) },
    fieldUnitName:function(i){ return FIELD_UNIT_KEYS[i] ? this.$t('tool.CronGenerator.'+FIELD_UNIT_KEYS[i]) : '' },
    fieldRangeHint:function(i){ return FIELD_RANGEHINTS[i] },
    presetsFor:function(key){ return this._presetCache[key]||[] },
    isPresetActive:function(f,p){
      if(!p.type)return false
      if(p.type===PRESET_TYPES.star)return f.mode==='*'
      if(p.type===PRESET_TYPES.last)return f.mode==='custom'&&f.customType==='last'
      if(p.type===PRESET_TYPES.step){
        return f.mode==='custom'&&f.customType==='step'&&f.stepFrom===p.stepFrom&&f.stepBy===p.stepBy
      }
      if(p.type===PRESET_TYPES.at&&p.atValue){
        return f.mode==='custom'&&f.customType==='at'&&f.specific===p.atValue
      }
      if(p.type===PRESET_TYPES.at||p.type===PRESET_TYPES.range){
        return f.mode==='custom'&&f.customType===p.type
      }
      return false
    },
    applyFieldPreset:function(f,p){
      if(!p.type)return
      if(p.type===PRESET_TYPES.star){f.mode='*';f.customType=''}
      else if(p.type===PRESET_TYPES.last){f.mode='custom';f.customType='last'}
      else if(p.type===PRESET_TYPES.step){f.mode='custom';f.customType='step';f.stepFrom=p.stepFrom||0;f.stepBy=p.stepBy||1}
      else if(p.type===PRESET_TYPES.at&&p.atValue){f.mode='custom';f.customType='at';f.specific=p.atValue}
      else if(p.type===PRESET_TYPES.at){f.mode='custom';f.customType='at'}
      else if(p.type===PRESET_TYPES.range){f.mode='custom';f.customType='range'}
      this.updateCron()
    },

    fieldExpr:function(f){
      if(f.mode==='*')return'*'
      if(f.mode!=='custom')return'*'
      if(f.customType==='last')return'L'
      if(f.customType==='every')return'*/'+(f.everyN||1)
      if(f.customType==='at'){var v=(f.specific||'').trim();return v||'*'}
      if(f.customType==='range'){var s=f.rangeStep>1?'/'+f.rangeStep:'';return f.rangeFrom+'-'+f.rangeTo+s}
      if(f.customType==='step'){var b=f.stepBy>1?'/'+f.stepBy:'';return (f.stepFrom||0)+b}
      return'*'
    },

    updateCron:function(){
      this.cronExpr=this.fields.map(this.fieldExpr).join(' ')
      this._suppressInput=true
      this.exprError=''
      this.buildDesc()
      this.calcNext()
      var self=this;this.$nextTick(function(){self._suppressInput=false})
    },

    onExprInput:function(){
      if(this._suppressInput)return
      this.exprError=''
      this.description=''
    },
    parseInput:function(){
      var raw=this.cronExpr.trim()
      if(!raw)return
      var parts=raw.split(/\s+/)
      if(parts.length<5){this.exprError=this.$t('tool.CronGenerator.errNeedFields');return}
      if(parts.length===5){parts.unshift('0')}
      if(parts.length>7){this.exprError=this.$t('tool.CronGenerator.errTooManyFields');return}
      if(parts.length===7){parts.pop()}

      var bounds=[[0,59],[0,59],[0,23],[1,31],[1,12],[0,6]]
      for(var i=0;i<6;i++){if(!this.validField(parts[i],bounds[i][0],bounds[i][1])){this.markError(i,bounds[i]);return}}

      this.exprError=''
      for(var j=0;j<6;j++){this.parseFieldToCfg(parts[j],this.fields[j])}
      this.cronExpr=this.fields.map(this.fieldExpr).join(' ')
      this.buildDesc()
      this.calcNext()
    },
    validField:function(v,min,max){
      if(v==='*'||v==='?'||v==='L')return true
      if(/^\d+$/.test(v)&&parseInt(v)>=min&&parseInt(v)<=max)return true
      if(/^\*\/\d+$/.test(v))return true
      if(/^\d+-\d+$/.test(v)){var p=v.split('-');return parseInt(p[0])>=min&&parseInt(p[1])<=max}
      if(/^[\d,]+$/.test(v))return v.split(',').every(function(x){var n=parseInt(x);return n>=min&&n<=max})
      if(/^\d+\/\d+$/.test(v))return true
      return false
    },
    markError:function(i,bound){
      var names=this.$t('tool.CronGenerator.sec')+','+this.$t('tool.CronGenerator.min')+','+this.$t('tool.CronGenerator.hour')+','+this.$t('tool.CronGenerator.day')+','+this.$t('tool.CronGenerator.month')+','+this.$t('tool.CronGenerator.week')
      var na=names.split(',')
      this.exprError=this.$t('tool.CronGenerator.errFieldFormat',{n:i+1,name:na[i],min:bound[0],max:bound[1]})
    },

    parseFieldToCfg:function(v,f){
      f.specific='';f.everyN=1;f.rangeFrom=0;f.rangeTo=59
      if(v==='*'||v==='?'){f.mode='*';f.customType='';return}
      if(v==='L'){f.mode='custom';f.customType='last';return}
      if(v.indexOf('*/')===0){f.mode='custom';f.customType='every';f.everyN=parseInt(v.slice(2))||1;return}
      if(v.indexOf('-')>=0){
        f.mode='custom';f.customType='range'
        var parts=v.split('-');f.rangeFrom=parseInt(parts[0])||0
        var to=parts[1];if(to.indexOf('/')>=0){var sp=to.split('/');to=sp[0];f.rangeStep=parseInt(sp[1])||1}
        else f.rangeStep=1
        f.rangeTo=parseInt(to)||59;return
      }
      if(/^\d+\/\d+$/.test(v)){f.mode='custom';f.customType='step';var sp2=v.split('/');f.stepFrom=parseInt(sp2[0]);f.stepBy=parseInt(sp2[1])||1;return}
      if(v.indexOf(',')>=0||/^\d+$/.test(v)){f.mode='custom';f.customType='at';f.specific=v;return}
      f.mode='*';f.customType=''
    },

    buildDesc:function(){
      var f=this.fields,p=[],t=this,wm=t.i18nWeekMap
      var s=f[0],m=f[1],h=f[2],d=f[3],mo=f[4],w=f[5]

      if(s.mode==='*'||(s.customType==='step'&&s.stepFrom===0&&s.stepBy===1)){}
      else if(s.mode==='custom'&&s.customType==='step')p.push(t.$t('tool.CronGenerator.sec')+s.stepBy+t.$t('common.unit.times'))
      else if(s.mode==='custom'&&s.customType==='at')p.push(t.$t('common.status.empty')+s.specific+t.$t('tool.CronGenerator.sec'))

      if(m.mode==='*'&&s.mode==='*')p.push(t.$t('tool.CronGenerator.descEveryMin'))
      else if(m.mode==='custom'&&m.customType==='step')p.push(t.$t('tool.CronGenerator.min')+m.stepBy+t.$t('common.unit.times'))
      else if(m.mode==='custom'&&m.customType==='at'&&m.specific)
        p.push(t.$t('tool.CronGenerator.descEveryHourMin',{min:m.specific}))
      else if(m.mode==='custom'&&m.customType==='range')
        p.push(t.$t('tool.CronGenerator.hour')+m.rangeFrom+'-'+m.rangeTo+t.$t('tool.CronGenerator.min'))

      if(h.mode==='custom'&&h.customType==='step')p.push(h.stepBy+'h')
      else if(h.mode==='custom'&&h.customType==='at'&&h.specific)
        p.push(h.specific+':'+'00')
      else if(h.mode==='custom'&&h.customType==='range')
        p.push(h.rangeFrom+'-'+h.rangeTo+'h')

      if(d.mode==='custom'&&d.customType==='last')p.push('L')
      else if(d.mode==='custom'&&d.customType==='at')p.push(t.$t('tool.CronGenerator.day')+d.specific)
      else if(d.mode==='custom'&&d.customType==='range')p.push(t.$t('tool.CronGenerator.day')+d.rangeFrom+'-'+d.rangeTo)

      if(mo.mode==='custom'&&mo.customType==='at')p.push(mo.specific)
      else if(mo.mode==='custom'&&mo.customType==='range')p.push(mo.rangeFrom+'-'+mo.rangeTo)

      if(w.mode==='custom'&&w.customType==='at'){
        var vals=w.specific.split(',').map(function(x){var i=parseInt(x.trim());return wm[i]||x}).join(',')
        if(w.specific==='1,2,3,4,5')p.push(t.$t('tool.CronGenerator.descWorkday'))
        else if(w.specific==='0,6')p.push(t.$t('tool.CronGenerator.descWeekend'))
        else p.push(vals)
      }else if(w.mode==='custom'&&w.customType==='range'){
        p.push(wm[w.rangeFrom]+'-'+wm[w.rangeTo])
      }

      var desc=p.join(',')
      if(!desc){desc=t.$t('tool.CronGenerator.descEverySec')}
      else{desc+=' '+t.$t('tool.CronGenerator.descExecute')}
      this.description=desc
    },

    parseField:function(v,min,max){
      if(v==='*'||v==='?'){var a=[];for(var i=min;i<=max;i++)a.push(i);return a}
      if(v==='L')return[999]
      if(v.indexOf('*/')===0){var step=parseInt(v.slice(2))||1;var r=[];for(var j=min;j<=max;j+=step)r.push(j);return r}
      if(v.indexOf('-')>=0){
        var parts=v.split('-'),s=parseInt(parts[0]),e=parseInt(parts[1]),st2=1;
        if(String(e).indexOf('/')>=0){var sp=String(e).split('/');e=parseInt(sp[0]);st2=parseInt(sp[1])||1}
        var r2=[];for(var k=s;k<=e;k+=st2)r2.push(k);return r2
      }
      if(/^\d+\/\d+$/.test(v)){var sp3=v.split('/');var st=parseInt(sp3[0]),by=parseInt(sp3[1]);var r3=[];for(var l=st;l<=max;l+=by)r3.push(l);return r3}
      return v.split(',').map(function(x){return parseInt(x.trim())}).filter(function(x){return!isNaN(x)})
    },
    lastDayOfMonth:function(y,m){return new Date(y,m,0).getDate()},
    calcNext:function(){
      try{
        var parts=this.cronExpr.split(/\s+/)
        var secs=this.parseField(parts[0],0,59),mins=this.parseField(parts[1],0,59),
          hours=this.parseField(parts[2],0,23),days=this.parseField(parts[3],1,31),
          months=this.parseField(parts[4],1,12),weeks=this.parseField(parts[5],0,6)
        var hasLast=(parts[3]==='L')
        var d=new Date();d.setMilliseconds(0);d.setSeconds(d.getSeconds()+1)
        var times=[],maxLoop=2*366*24*60*60,self=this;
        for(var i=0;i<maxLoop&&times.length<5;i++){
          var y=d.getFullYear(),mo=d.getMonth()+1,da=d.getDate(),
            h=d.getHours(),mi=d.getMinutes(),se=d.getSeconds(),dw=d.getDay()
          var dayOk
          if(hasLast){dayOk=da===self.lastDayOfMonth(y,mo)}else{dayOk=days.includes(da)||weeks.includes(dw)}
          if(parts[3]==='*')dayOk=days.includes(da)||weeks.includes(dw)
          if(months.includes(mo)&&dayOk&&hours.includes(h)&&mins.includes(mi)&&secs.includes(se)){
            times.push(self.fmtDate(d))
          }
          d.setSeconds(d.getSeconds()+1)
        }
        this.nextTimes=times
      }catch(e){this.nextTimes=[]}
    },
    fmtDate:function(d){
      try{
        return new Intl.DateTimeFormat(navigator.language||'en',{
          timeZone:this.timezone,year:'numeric',month:'2-digit',day:'2-digit',
          weekday:'short',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:false
        }).format(d)
      }catch(e){
        return d.getFullYear()+'-'+pad(d.getMonth()+1)+'-'+pad(d.getDate())+' '+pad(d.getHours())+':'+pad(d.getMinutes())+':'+pad(d.getSeconds())
      }
    },

    copyExpr:function(){
      var self=this
      navigator.clipboard.writeText(this.cronExpr).then(function(){
        self.$message({message:self.$t('tool.CronGenerator.copied'),type:'success',duration:1200})
      }).catch(function(){
        self.$message.error(self.$t('tool.CronGenerator.copyFailed'))
      })
    },
    doAction:function(a){if(a==='copy')this.copyExpr()}
  }
}

function pad(n){return n<10?'0'+n:''+n}
</script>

<style scoped>
.cron-app { max-width: 100%; display: flex; flex-direction: column; gap: 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }

/* ===== Cards ===== */
.cron-card { background: #fff; border: 1px solid #e8ecf1; border-radius: 16px; padding: 24px 28px; transition: border-color .15s, box-shadow .15s; }
.cron-card:hover { border-color: #d0d7e5; box-shadow: 0 2px 12px rgba(0,0,0,.04); }
.cron-card-head { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
.cron-card-icon { font-size: 18px; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; background: #f5f7fa; border-radius: 10px; flex-shrink: 0; }
.cron-card-title { font-size: 15px; font-weight: 700; color: #1a1f36; }
.cron-card-hint { font-size: 12px; color: #b0b8c4; margin-left: auto; }

/* ===== Fields ===== */
.cron-fields { display: flex; flex-direction: column; }
.cron-field { padding: 20px 0; }
.cron-field:first-child { padding-top: 0; }
.cron-field:last-child { padding-bottom: 0; }
.cron-field-hd { display: flex; align-items: baseline; gap: 10px; margin-bottom: 10px; }
.cron-field-name { font-size: 14px; font-weight: 700; color: #303133; min-width: 48px; }
.cron-field-range { font-size: 11px; color: #c0c4cc; font-family: 'SF Mono', Menlo, Consolas, monospace; }

/* Preset chips */
.cron-field-presets { display: flex; flex-wrap: wrap; gap: 8px; }
.cron-chip {
  padding: 7px 16px; border-radius: 8px; font-size: 12px; font-weight: 500; cursor: pointer;
  border: 1.5px solid #e1e5e9; background: #fafbfc; color: #606266; transition: all .15s; outline: none; user-select: none;
}
.cron-chip:hover { background: #f0f4ff; border-color: #b8c8f0; color: #4e7cff; transform: translateY(-1px); box-shadow: 0 2px 6px rgba(78,124,255,.08); }
.cron-chip.on { background: linear-gradient(135deg, #e8f0fe, #dfebff); border-color: #4e7cff; color: #4e7cff; font-weight: 600; box-shadow: 0 2px 8px rgba(78,124,255,.12); }

/* Custom params */
.cron-field-custom { margin-top: 10px; animation: cronFadeIn .15s; }
@keyframes cronFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.cron-custom-row { display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: #f8f9fb; border-radius: 10px; font-size: 13px; border: 1px solid #ebeef5; }
.cron-custom-lbl { color: #909399; font-size: 12px; font-weight: 500; white-space: nowrap; }
.cron-custom-hint { color: #c0c4cc; font-size: 11px; margin-left: 4px; }

.cron-inp { border: 1.5px solid #dcdfe6; border-radius: 8px; padding: 8px 12px; font-size: 13px; outline: none; color: #303133; font-family: 'SF Mono', Menlo, Consolas, monospace; text-align: center; transition: all .15s; background: #fff; }
.cron-inp:focus { border-color: #4e7cff; box-shadow: 0 0 0 3px rgba(78,124,255,.08); }
.cron-inp-num { width: 68px; }
.cron-inp-txt { width: 150px; }

/* ===== Expression ===== */
.cron-expr-body { display: flex; flex-direction: column; gap: 12px; }
.cron-expr-display { display: flex; align-items: center; gap: 10px; }
.cron-expr-input {
  flex: 1; border: 2px solid #e1e5e9; border-radius: 12px; padding: 14px 18px;
  font-size: 20px; font-family: 'SF Mono', Menlo, Consolas, monospace; font-weight: 700;
  color: #303133; outline: none; background: #fafbfc; letter-spacing: 1px; transition: all .15s;
}
.cron-expr-input:focus { border-color: #4e7cff; background: #fff; box-shadow: 0 0 0 4px rgba(78,124,255,.06); }
.cron-expr-input.error { border-color: #e74c3c; background: #fff5f5; }
.cron-expr-input::placeholder { color: #d0d4dc; font-weight: 400; font-size: 15px; letter-spacing: 0; }
.cron-btn-copy {
  display: flex; align-items: center; justify-content: center; width: 46px; height: 46px;
  border: 1.5px solid #e1e5e9; border-radius: 12px; background: #fafbfc; color: #8892a6;
  cursor: pointer; transition: all .15s; flex-shrink: 0;
}
.cron-btn-copy:hover { background: #f0f4ff; border-color: #4e7cff; color: #4e7cff; transform: scale(1.05); }

.cron-expr-err { font-size: 13px; color: #e74c3c; padding: 8px 14px; background: #fff5f5; border-radius: 10px; border: 1px solid #fde0e0; }

.cron-desc { display: flex; align-items: flex-start; gap: 10px; font-size: 15px; color: #2e7d32; padding: 14px 18px; background: #f0f9eb; border-radius: 12px; border: 1px solid #e1f3d8; line-height: 1.6; }
.cron-desc-icon { flex-shrink: 0; font-size: 18px; }

/* ===== Next runs ===== */
.cron-tz-picker { display: flex; align-items: center; gap: 8px; margin-left: auto; }
.cron-tz-lbl { font-size: 12px; color: #909399; font-weight: 500; }
.cron-result-card ::v-deep .el-select .el-input__inner { border-radius: 8px; }

.cron-next-list { display: flex; flex-direction: column; gap: 8px; }
.cron-next-item { display: flex; align-items: center; gap: 14px; padding: 14px 18px; background: #f8f9fb; border-radius: 12px; border: 1px solid transparent; transition: all .15s; }
.cron-next-item:hover { background: #f0f4ff; border-color: #d6e0f5; }
.cron-next-dot {
  display: flex; align-items: center; justify-content: center; min-width: 30px; height: 30px;
  border-radius: 50%; background: linear-gradient(135deg, #e8f0fe, #dfebff); color: #4e7cff;
  font-size: 12px; font-weight: 700; font-family: 'SF Mono', Menlo, Consolas, monospace; flex-shrink: 0;
}
.cron-next-time { font-size: 14px; color: #303133; font-family: 'SF Mono', Menlo, Consolas, monospace; letter-spacing: .3px; }
.cron-next-empty { font-size: 13px; color: #b0b8c4; padding: 20px 16px; text-align: center; background: #fafbfc; border-radius: 10px; }

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .cron-card { padding: 16px 18px; }
  .cron-field-presets { gap: 6px; }
  .cron-chip { padding: 6px 12px; font-size: 11px; }
  .cron-expr-input { font-size: 16px; padding: 12px 14px; }
  .cron-next-time { font-size: 12px; }
  .cron-custom-row { flex-wrap: wrap; }
}
</style>
